import {
  AuthError,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  or,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";

import { VerifiedStatus } from "../constants/UserConstants";
import UserType from "../constants/UserType";
import removeUndefinedFields from "../helper/removeUndefinedFields";
import User from "../interfaces/User";
import { sendEmailRequest } from "./email";
import { auth, db, firebaseCloudMessaging } from "./firebase";
import { createUserLog } from "./logs";
import { removeFCMToken } from "./notification";
import { addSettingsData, updateGeoHash } from "./user";

const createAccountWithEmailAndPassword = async (
  email: string,
  password: string,
  userData: User
) => {
  console.log("🔐 createAccountWithEmailAndPassword called");
  const userDataCopy = JSON.parse(JSON.stringify(userData));
  try {
    console.log("📝 Creating Firebase auth user...");
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    console.log("✅ Firebase auth user created, UID:", user.uid);
    delete userDataCopy?.password;
    console.log("📝 Adding user to database...");
    await addNewUserToDB(user.uid, userDataCopy);
    console.log("📝 Adding settings data...");
    await addSettingsData(user.uid);
    console.log("📝 Creating account log for user:", user.uid);
    const logResult = await createUserLog(
      user.uid,
      "Account Created",
      `Account created with email/password. User type: ${userDataCopy.userType}`,
      { email, userType: userDataCopy.userType },
      "Email/Password Signup"
    );
    console.log("✅ Account creation log created successfully:", logResult.id);
    toast.success("Account created successfully");
    return true;
  } catch (error) {
    console.error("❌ ERROR in createAccountWithEmailAndPassword:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
    toast.error("Account creation failed");
    return false;
  }
};
const createAccountWithEmailLink = async (
  email: string,
  uid: string,
  userData: User
) => {
  const userDataCopy = JSON.parse(JSON.stringify(userData));

  try {
    console.log(userDataCopy);

    await addNewUserToDB(uid, userDataCopy);
    await addSettingsData(uid);
    await createUserLog(
      uid,
      "Account Created",
      `Account created with email link. User type: ${userDataCopy.userType}`,
      { email, userType: userDataCopy.userType },
      "Email Link Signup"
    );
    toast.success("Account created successfully");
    return true;
  } catch (error) {
    toast.error("Account creation failed");
    console.log(error);

    return false;
  }
};
const createAccountWithPhoneNumber = async (
  phoneNumber: string,
  userUid: string,
  user: User
) => {
  const userData = {
    ...user,
    phoneNumber,
    createdAt: serverTimestamp(),
    disabled: false,
    verified: VerifiedStatus.False,
    noOfTimeLogin: 1,
    contractSigned: false,
  };
  const docRef = doc(db, "users", userUid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    toast.info("This phone number already has an account. You're logged in.");
    return false;
  } else {
    await setDoc(doc(db, "users", userUid), { ...userData });
    await createUserLog(
      userUid,
      "Account Created",
      `Account created with phone number. User type: ${user.userType}`,
      { phoneNumber, userType: user.userType },
      "Phone Number Signup"
    );
    toast.success("Successfully create account.");
    return true;
  }
};

const loginUserWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await updateNumberOfLoginTimes((result.user as User).uid);
    toast.success("Login successful");
    await createUserLog(
      result.user.uid,
      "Login",
      "User logged in successfully with email/password",
      { email },
      "Email/Password Login"
    );
    return true;
  } catch (error) {
    toast.error("Login failed");
    return false;
  }
};

const updateNumberOfLoginTimes = async (userId?: string) => {
  if (userId) {
    const docRef = doc(db, `users/${userId}`);
    await updateDoc(docRef, {
      noOfTimeLogin: increment(1),
    });
  }
};

const assignReferredByCode = async (userId: string, userData: User) => {
  const referralCode = localStorage.getItem("referralCode");

  if (referralCode) {
    // Update the current user with the referral code in the referredBy field
    await updateDoc(doc(db, `users/${userId}`), {
      referredBy: referralCode,
    });

    // Find the referred user by their document ID (which is referralCode in this case)
    const referredUserDocRef = doc(db, `users`, referralCode);

    // Check if the referred user exists
    const referredUserDocSnap = await getDoc(referredUserDocRef);

    if (referredUserDocSnap.exists()) {
      const referredUserData = referredUserDocSnap.data();
      // Update the referredTo field (array) of the referred user document
      await updateDoc(referredUserDocRef, {
        referredTo: arrayUnion(userId),
      });

      sendEmailRequest(referredUserDocSnap.id, "RE1C", [
        {
          key: "referredUserName",
          value: userData.firstName + " " + userData.lastName,
        },
        {
          key: "chefName",
          value: referredUserData.firstName + " " + referredUserData.lastName,
        },
      ]);
    } else {
      console.log("Referred user document does not exist.");
    }
  }
};

const addNewUserToDB = async (userUid: string, userData: User) => {
  const user = removeUndefinedFields({
    ...userData,
    address: userData?.address,
    userType: userData.userType,
    dob: userData?.dob && Timestamp.fromDate(new Date(userData?.dob)),
    firstName: userData?.firstName,
    lastName: userData?.lastName,
    email: userData?.email?.toLowerCase(),
    phoneNumber: userData?.phoneNumber,
    createdAt: serverTimestamp(),
    disabled: false,
    verified: VerifiedStatus.False,
    noOfTimeLogin: 1,
    // contractSigned: userData.userType === UserType.Chef ? true : false,
    contractSigned: false,
  });
  await setDoc(doc(db, "users", userUid), user);
  userData.address?.lat &&
    userData.address.lng &&
    updateGeoHash(
      `users/${userUid}`,
      userData?.address?.lat,
      userData?.address.lng
    );

  assignReferredByCode(userUid, userData);
};

const signOutUser = async () => {
  console.log("🚪 signOutUser called");
  const getFCMToken = await firebaseCloudMessaging.tokenInLocalStorage();
  const currentUid = auth.currentUser?.uid;
  console.log("👤 Current user UID:", currentUid);

  // Create log BEFORE signOut (while user is still authenticated)
  if (currentUid) {
    try {
      console.log("📝 Creating logout log for user:", currentUid);
      const logResult = await createUserLog(
        currentUid,
        "Logout",
        "User logged out successfully",
        {},
        "User Logout"
      );
      console.log("✅ Logout log created successfully:", logResult.id);
    } catch (error) {
      console.error("❌ Error creating logout log:", error);
      // Log the full error details
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
    }
  } else {
    console.warn("⚠️ No current user UID found for logout log");
  }

  if (currentUid && getFCMToken) {
    await removeFCMToken(currentUid, getFCMToken);
  }

  localStorage.clear();

  try {
    await signOut(auth);
    toast.success("Sign out successful", {
      toastId: "signOutSuccessfully",
    });
  } catch (error) {
    toast.error("Sign out failed");
  }
};

const provider = new GoogleAuthProvider();
const loginWithGoogle = async () => {
  // TODO: Check if user already exists in the database
  // If user exists, sign in with Google
  const data: { name?: string; email?: string; shouldRedirect?: boolean } = {};
  const result = await signInWithPopup(auth, provider);
  if (result.user.email) {
    const isExist = await checkEmailIsExist(result.user.email);
    console.log(auth.currentUser);
    if (!isExist && auth.currentUser) {
      data.email = auth.currentUser?.email
        ? auth.currentUser?.email
        : undefined;
      data.name = auth.currentUser?.displayName
        ? auth.currentUser?.displayName
        : undefined;
      await auth.currentUser.delete();
      data.shouldRedirect = true;
      toast.error("Account is not present. Try to sign up.");
      return data;
    } else {
      if (auth.currentUser?.uid) {
        await createUserLog(
          auth.currentUser.uid,
          "Login",
          "User logged in successfully with Google",
          { email: result.user.email },
          "Google Login"
        );
      }
      data.shouldRedirect = false;
      return data;
    }
  }
};

const signupWithGoogle = async (
  firstName: string,
  lastName: string,
  userType: UserType
) => {
  const result = await signInWithPopup(auth, provider);
  if (result.user.email) {
    const isExist = await checkEmailIsExist(result.user.email);
    console.log(auth.currentUser);
    if (!isExist && auth.currentUser) {
      const email = auth.currentUser?.email
        ? auth.currentUser?.email
        : undefined;
      const user: User = {
        email: email,
        firstName: firstName,
        lastName: lastName,
        userType: userType,
        publicName: `${firstName} ${lastName}`,
      };
      await addNewUserToDB(auth.currentUser.uid, user);
      await createUserLog(
        auth.currentUser.uid,
        "Account Created",
        `Account created with Google. User type: ${userType}`,
        { email, userType },
        "Google Signup"
      );
      toast.success("Account created successfully.");
      return true;
    } else {
      if (auth.currentUser?.uid) {
        await createUserLog(
          auth.currentUser.uid,
          "Login",
          "User logged in successfully with Google",
          { email: result.user.email },
          "Google Login"
        );
      }
      toast.success("Email already exist, Login successful.");
      return false;
    }
  }
};

const checkEmailIsExist = async (email: string) => {
  const usersRef = collection(db, `users`);
  const selectedUserRef = query(
    usersRef,
    or(where("email", "==", email), where("email", "==", email.toLowerCase()))
  );
  const Snapshot = await getDocs(selectedUserRef);
  return Snapshot.size > 0;
};

const checkPhoneNumberIsExist = async (phoneNumber: string) => {
  const usersRef = collection(db, `users`);
  const selectedUserRef = query(
    usersRef,
    where("phoneNumber", "==", phoneNumber)
  );
  const Snapshot = await getDocs(selectedUserRef);
  return Snapshot.size > 0;
};

const linkEmailWithPhoneNumber = async (email: string) => {
  const user = auth.currentUser;
  const link = window.location.href;
  if (isSignInWithEmailLink(auth, link) && user) {
    try {
      // 5. Create the credential using the email and the link
      const credential = EmailAuthProvider.credentialWithLink(email, link);

      // 6. Link this new credential to the existing user
      await linkWithCredential(user, credential);
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        email: email, // The email we just linked
      });
    } catch (err) {
      const authError = err as AuthError;
      console.error(authError.code, authError.message);

      if (authError.code === "auth/email-already-in-use") {
        console.error("This email is already linked to another account.");
      } else {
        console.error("An error occurred. Please try the process again.");
      }
    }
  }
};

export {
  checkEmailIsExist,
  checkPhoneNumberIsExist,
  createAccountWithEmailAndPassword,
  createAccountWithEmailLink,
  createAccountWithPhoneNumber,
  linkEmailWithPhoneNumber,
  loginUserWithEmailAndPassword,
  loginWithGoogle,
  signOutUser,
  signupWithGoogle,
};
